@use "styles/abstracts/variables" as v;

.mapWrapper {
  --map-void: #070b12;
  --map-star: rgba(210, 220, 230, 0.5);
  --map-accent-rgb: 0, 84, 231;
  --map-route: #{v.$palette-sea-tracking};
  --map-grid: rgba(var(--map-accent-rgb), 0.13);
  --map-grid-bold: rgba(var(--map-accent-rgb), 0.06);
  --map-glow: rgba(var(--map-accent-rgb), 0.08);
  --map-aurora-a: rgba(var(--map-accent-rgb), 0.08);
  --map-aurora-b: rgba(var(--map-accent-rgb), 0.06);
  --map-aurora-c: rgba(var(--map-accent-rgb), 0.05);
  --map-route-opacity: 0.26;
  --map-flow-opacity: 0.28;
  --map-packet-opacity: 0.35;
  --map-dot-opacity: 0.95;
  --map-halo-opacity: 0.2;
  --map-void-tint: 10%;

  flex-grow: 1;
  display: flex;
  position: relative;
  overflow: hidden;
  background-color: transparent;

  &.isGlobe {
    background-color: var(--map-void);
  }
}

.isAir {
  --map-accent-rgb: 245, 44, 92;
  --map-route: #{v.$palette-red};
  --map-glow: rgba(var(--map-accent-rgb), 0.06);
  --map-aurora-a: rgba(var(--map-accent-rgb), 0.06);
  --map-aurora-b: rgba(var(--map-accent-rgb), 0.045);
  --map-aurora-c: rgba(var(--map-accent-rgb), 0.04);
  --map-halo-opacity: 0.15;
}

.isGlobal {
  --map-accent-rgb: 26, 172, 68;
  --map-route: rgb(26, 172, 68);
  --map-glow: rgba(var(--map-accent-rgb), 0.09);
  --map-aurora-a: rgba(var(--map-accent-rgb), 0.09);
  --map-aurora-b: rgba(var(--map-accent-rgb), 0.07);
  --map-aurora-c: rgba(var(--map-accent-rgb), 0.055);
  --map-halo-opacity: 0.22;
}

:global([data-theme="light"]) .mapWrapper {
  --map-void: color-mix(in srgb, #{v.$palette-gray-100} calc(100% - var(--map-void-tint)), rgb(var(--map-accent-rgb)) var(--map-void-tint));
  --map-star: rgba(0, 12, 55, 0.2);
  --map-grid: rgba(var(--map-accent-rgb), 0.18);
  --map-grid-bold: rgba(var(--map-accent-rgb), 0.09);
  --map-glow: rgba(var(--map-accent-rgb), 0.12);
  --map-aurora-a: rgba(var(--map-accent-rgb), 0.12);
  --map-aurora-b: rgba(var(--map-accent-rgb), 0.1);
  --map-aurora-c: rgba(var(--map-accent-rgb), 0.08);
  --map-route-opacity: 0.45;
  --map-flow-opacity: 0.4;
  --map-packet-opacity: 0.55;
  --map-dot-opacity: 0.9;
  --map-halo-opacity: 0.16;
}

:global([data-theme="light"]) .isAir {
  --map-void-tint: 8%;
  --map-glow: rgba(var(--map-accent-rgb), 0.09);
  --map-aurora-a: rgba(var(--map-accent-rgb), 0.09);
  --map-aurora-b: rgba(var(--map-accent-rgb), 0.07);
  --map-aurora-c: rgba(var(--map-accent-rgb), 0.06);
  --map-halo-opacity: 0.13;
}

:global([data-theme="light"]) .isGlobal {
  --map-void-tint: 9%;
  --map-glow: rgba(var(--map-accent-rgb), 0.13);
  --map-aurora-a: rgba(var(--map-accent-rgb), 0.13);
  --map-aurora-b: rgba(var(--map-accent-rgb), 0.11);
  --map-aurora-c: rgba(var(--map-accent-rgb), 0.09);
  --map-halo-opacity: 0.18;
}

.backdrop {
  --pointer-x: 0.5;
  --pointer-y: 0.5;
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.35s ease,
    visibility 0.35s ease;
}

.isGlobe .backdrop {
  opacity: 1;
  visibility: visible;
}

.ambient,
.grid,
.cursorGlow,
.backdropSvg {
  position: absolute;
  inset: 0;
}

.ambient {
  background:
    radial-gradient(ellipse 55% 45% at 18% 12%, var(--map-aurora-a), transparent 62%),
    radial-gradient(ellipse 45% 55% at 88% 82%, var(--map-aurora-b), transparent 58%),
    radial-gradient(ellipse 40% 40% at 70% 20%, var(--map-aurora-c), transparent 70%);
  animation: ambientBreathe 22s ease-in-out infinite;
}

.grid {
  background-image:
    linear-gradient(var(--map-grid) 1px, transparent 1px),
    linear-gradient(90deg, var(--map-grid) 1px, transparent 1px),
    linear-gradient(var(--map-grid-bold) 1px, transparent 1px),
    linear-gradient(90deg, var(--map-grid-bold) 1px, transparent 1px);
  background-size:
    44px 44px,
    44px 44px,
    176px 176px,
    176px 176px;
  transform: translate(
    calc((var(--pointer-x) - 0.5) * -6px),
    calc((var(--pointer-y) - 0.5) * -5px)
  );
  animation: gridDrift 90s linear infinite;
}

.cursorGlow {
  background: radial-gradient(
    460px circle at calc(var(--pointer-x) * 100%) calc(var(--pointer-y) * 100%),
    var(--map-glow),
    transparent 58%
  );
}

.backdropSvg {
  display: block;
  width: 100%;
  height: 100%;
  color: var(--map-route);
  transition: color 0.4s ease;
  transform: translate(
    calc((var(--pointer-x) - 0.5) * 10px),
    calc((var(--pointer-y) - 0.5) * 8px)
  );
}

.stars {
  color: var(--map-star);
}

.star {
  animation: twinkle 7s ease-in-out infinite;
}

.backdropRoutes {
  stroke-width: 1;
  stroke-linecap: round;
  opacity: var(--map-route-opacity);
}

.routeFlows {
  opacity: var(--map-flow-opacity);
}

.routeFlow {
  stroke-width: 1;
  stroke-linecap: round;
  stroke-dasharray: 0.05 0.95;
  animation: routeFlow 16s linear infinite;
}

.packets {
  opacity: var(--map-packet-opacity);
}

.packet {
  animation: packetPulse 4s ease-in-out infinite;
}

.backdropDots {
  opacity: var(--map-dot-opacity);
}

.backdropDotHalo {
  opacity: var(--map-halo-opacity);
}

.dot {
  animation: twinkle 6.5s ease-in-out infinite;
}

.pulseRing {
  fill: none;
  stroke: currentColor;
  stroke-width: 0.8;
  transform-box: fill-box;
  transform-origin: center;
  animation: pulseRing 6.5s ease-out infinite;
}

.mapLayer {
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  min-height: 0;
}


@keyframes ambientBreathe {
  0%,
  100% {
    opacity: 0.85;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
}

@keyframes gridDrift {
  to {
    background-position:
      44px 44px,
      44px 44px,
      176px 176px,
      176px 176px;
  }
}

@keyframes routeFlow {
  to {
    stroke-dashoffset: -1;
  }
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

@keyframes pulseRing {
  0% {
    transform: scale(1);
    opacity: 0.22;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
}

@keyframes packetPulse {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 0.8;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ambient,
  .grid,
  .star,
  .routeFlow,
  .packet,
  .dot,
  .pulseRing {
    animation: none;
  }

  .grid,
  .backdropSvg {
    transform: none;
  }
}
